/**
 * Shared react-select styling.
 *
 * react-select paints its controls through emotion, and those runtime styles
 * beat the rules in `scss/`. Every dropdown therefore has to be handed the same
 * `styles` object to look like the native `.input-text` / `.input-select`
 * fields — that object lives here so the CSR and SCS wizards cannot drift apart
 * again (they previously carried four different focus colours between them).
 */

// Mirrors scss/variables.scss — keep the two in sync.
const FIELD_BORDER = "#b0bac4"; // $field-border
const FIELD_BORDER_HOVER = "#8e99a4"; // darken($field-border, 12%)
const CTA_DEFAULT = "#006dee"; // $cta-default
const FOCUS_RING = "0 0 0 3px rgba(0, 109, 238, 0.2)"; // $focus-ring
const OPTION_HOVER_BG = "rgba(0, 109, 238, 0.06)";
const OPTION_ACTIVE_BG = "rgba(0, 109, 238, 0.1)";
const TEXT_DEFAULT = "#111827"; // $text-default
const GREY = "#777e7e"; // $grey
const ERROR = "#ff3366"; // $error
const WHITE = "#fff"; // $white
const DISABLED_BG = "#f5f5f5";
const CHIP_BG = "#eef1f5";
const CHIP_REMOVE = "#6c757d";
const RADIUS_SM = "4px"; // $radius-sm
const RADIUS_MD = "6px"; // $radius-md
const TRANSITION_FAST = "0.15s ease-in-out"; // $transition-fast

/**
 * Height of a native field: 22px line-height + 10px padding top and bottom +
 * the 1px borders. Dropdowns sit beside inputs in the comparator rows, so they
 * have to resolve to the same 44px.
 */
export const SELECT_MIN_HEIGHT = "44px";

/** Entered/selected text — one font size and colour for every field. */
export const SELECT_FONT = {
  fontSize: "15px",
  lineHeight: "22px",
  fontWeight: 500,
  color: TEXT_DEFAULT,
};

/**
 * The canonical react-select `styles` object.
 *
 * @param {object}  [options]
 * @param {boolean} [options.isDisabled] dim and lock the control
 * @param {string}  [options.minHeight]  override only where a taller control is
 *                                       genuinely required
 */
export const buildSelectStyles = ({
  isDisabled = false,
  minHeight = SELECT_MIN_HEIGHT,
} = {}) => ({
  container: provided => ({
    ...provided,
    width: "100%",
  }),
  control: (provided, state) => ({
    ...provided,
    ...SELECT_FONT,
    minHeight,
    borderRadius: RADIUS_MD,
    border: `1px solid ${state.isFocused ? CTA_DEFAULT : FIELD_BORDER}`,
    boxShadow: state.isFocused ? FOCUS_RING : "none",
    backgroundColor: isDisabled ? DISABLED_BG : WHITE,
    transition: `border-color ${TRANSITION_FAST}, box-shadow ${TRANSITION_FAST}`,
    "&:hover": {
      borderColor: state.isFocused ? CTA_DEFAULT : FIELD_BORDER_HOVER,
    },
    opacity: isDisabled ? 0.7 : 1,
    pointerEvents: isDisabled ? "none" : "auto",
    cursor: isDisabled ? "not-allowed" : "default",
  }),
  valueContainer: provided => ({
    ...provided,
    padding: "2px 12px",
  }),
  singleValue: provided => ({
    ...provided,
    ...SELECT_FONT,
  }),
  input: provided => ({
    ...provided,
    ...SELECT_FONT,
  }),
  placeholder: provided => ({
    ...provided,
    ...SELECT_FONT,
    color: GREY,
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: CHIP_BG,
    borderRadius: RADIUS_SM,
    margin: "3px 4px 3px 0",
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: TEXT_DEFAULT,
    fontSize: "13px",
    padding: "3px 6px",
  }),
  multiValueRemove: provided => ({
    ...provided,
    color: CHIP_REMOVE,
    borderRadius: `0 ${RADIUS_SM} ${RADIUS_SM} 0`,
    transition: `all ${TRANSITION_FAST}`,
    "&:hover": {
      backgroundColor: ERROR,
      color: WHITE,
    },
  }),
  menu: provided => ({
    ...provided,
    zIndex: 9999,
    borderRadius: RADIUS_MD,
    border: `1px solid ${FIELD_BORDER}`,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
  }),
  menuList: provided => ({
    ...provided,
    padding: "4px 0",
  }),
  option: (provided, state) => ({
    ...provided,
    ...SELECT_FONT,
    padding: "10px 14px",
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? CTA_DEFAULT
      : state.isFocused
        ? OPTION_HOVER_BG
        : "transparent",
    color: state.isSelected ? WHITE : TEXT_DEFAULT,
    "&:active": {
      backgroundColor: state.isSelected ? CTA_DEFAULT : OPTION_ACTIVE_BG,
    },
  }),
});

/** Ready-made styles for the common case (enabled control, standard height). */
export const SELECT_STYLES = buildSelectStyles();

/**
 * Layer per-page tweaks on top of the shared styles: each override receives the
 * object the base style produced, so it patches rather than replaces it.
 *
 * @param {object} base  result of `buildSelectStyles`
 * @param {object} extra react-select style callbacks to apply afterwards
 */
export const mergeSelectStyles = (base, extra = {}) =>
  Object.entries(extra).reduce(
    (merged, [key, override]) => ({
      ...merged,
      [key]: (provided, state) =>
        override(base[key] ? base[key](provided, state) : provided, state),
    }),
    { ...base }
  );
